import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageSquare,
  Brain,
  Mail,
  FolderOpen,
  FileText,
  Mic,
  Share2,
  Shield,
  Zap,
  BookOpen,
  ChevronRight,
  ArrowLeft,
  Search,
  Clock,
  Sparkles,
  Settings,
  CreditCard,
  Users,
  Lock,
  Globe,
  Lightbulb,
  Target,
  Layers
} from 'lucide-react';

const sections = [
  { id: 'overview', title: 'Overview', icon: BookOpen },
  { id: 'getting-started', title: 'Getting Started', icon: Zap },
  { id: 'chat', title: 'Chat & Conversations', icon: MessageSquare },
  { id: 'thinking-modes', title: 'Thinking Modes', icon: Brain },
  { id: 'memories', title: 'Memories', icon: Lightbulb },
  { id: 'knowledge-base', title: 'Knowledge Base', icon: FileText },
  { id: 'emails', title: 'Email Integration', icon: Mail },
  { id: 'folders', title: 'Organization & Folders', icon: FolderOpen },
  { id: 'voice', title: 'Voice Input', icon: Mic },
  { id: 'sharing', title: 'Sharing Conversations', icon: Share2 },
  { id: 'billing', title: 'Billing & Subscriptions', icon: CreditCard },
  { id: 'privacy', title: 'Privacy & Security', icon: Shield },
];

export default function DocsPage() {
  const [activeSection, setActiveSection] = useState('overview');

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Back to Arlo</span>
            </Link>
          </div>
          <h1 className="text-xl font-semibold">Documentation</h1>
          <Button variant="outline" size="sm" asChild>
            <Link to="/chat">Open Arlo</Link>
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {section.title}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            {/* Overview */}
            <section id="overview" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <BookOpen className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Overview</h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                Arlo is an AI personal assistant designed specifically for business professionals. 
                It helps you think, plan, write, and make decisions faster through a minimal, 
                distraction-free interface.
              </p>

              <div className="grid gap-4 md:grid-cols-2 mb-8">
                <div className="p-4 border border-border rounded-lg">
                  <Target className="h-5 w-5 text-emerald-500 mb-2" />
                  <h3 className="font-semibold mb-1">Purpose-Built</h3>
                  <p className="text-sm text-muted-foreground">
                    Designed for professionals who need to move fast and think clearly.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <Brain className="h-5 w-5 text-violet-500 mb-2" />
                  <h3 className="font-semibold mb-1">Learns Your Style</h3>
                  <p className="text-sm text-muted-foreground">
                    Arlo remembers your preferences and adapts to how you work.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <Layers className="h-5 w-5 text-blue-500 mb-2" />
                  <h3 className="font-semibold mb-1">Context-Aware</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload documents and Arlo uses them to give better answers.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <Shield className="h-5 w-5 text-amber-500 mb-2" />
                  <h3 className="font-semibold mb-1">Private & Secure</h3>
                  <p className="text-sm text-muted-foreground">
                    Your data is encrypted and never used for training.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <h3 className="font-semibold mb-2">Key Capabilities</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    Draft emails, documents, and communications in your voice
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    Analyze documents and extract key insights
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    Brainstorm ideas and explore different perspectives
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    Manage email threads with AI-assisted drafting
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-primary" />
                    Organize conversations into folders and projects
                  </li>
                </ul>
              </div>
            </section>

            {/* Getting Started */}
            <section id="getting-started" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <Zap className="h-6 w-6 text-emerald-500" />
                </div>
                <h2 className="text-3xl font-bold">Getting Started</h2>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Create Your Account</h3>
                    <p className="text-muted-foreground">
                      Sign up with your email address. You can use password authentication 
                      or magic link (passwordless) login for convenience.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Complete Onboarding</h3>
                    <p className="text-muted-foreground">
                      Tell Arlo about yourself—your role, industry, communication style, 
                      and what you need help with. This helps Arlo personalize responses.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Upload Your Knowledge Base</h3>
                    <p className="text-muted-foreground">
                      Optionally upload documents (PDFs, Word docs, text files) that Arlo 
                      can reference when helping you. Great for company policies, product 
                      info, or reference materials.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Start Chatting</h3>
                    <p className="text-muted-foreground">
                      That's it! Start a new conversation and ask Arlo anything. Use the 
                      quick-start prompts for inspiration or dive right in.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Chat & Conversations */}
            <section id="chat" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-blue-500" />
                </div>
                <h2 className="text-3xl font-bold">Chat & Conversations</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                The chat interface is where you'll spend most of your time with Arlo. 
                It's designed to be fast, focused, and distraction-free.
              </p>

              <h3 className="font-semibold text-lg mb-3">Creating Conversations</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>New Chat:</strong> Click "New chat" to start a fresh conversation. Arlo will auto-name it based on your first message.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Temporary Chat:</strong> Use this for quick, one-off questions. These chats aren't saved to your history.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Quick Prompts:</strong> Use pre-built prompts to get started quickly with common tasks.</span>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">Managing Conversations</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Rename:</strong> Click the title to rename any conversation.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Move to Folder:</strong> Organize conversations into folders for different projects or topics.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Search:</strong> Use the search bar to find past conversations by content or title.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Delete:</strong> Remove conversations you no longer need.</span>
                </li>
              </ul>

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <h4 className="font-semibold text-amber-500 mb-2">💡 Pro Tip</h4>
                <p className="text-sm text-muted-foreground">
                  Use keyboard shortcut <code className="bg-muted px-1 rounded">Shift + Enter</code> for 
                  new lines within your message without sending it.
                </p>
              </div>
            </section>

            {/* Thinking Modes */}
            <section id="thinking-modes" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-violet-500/10 rounded-lg">
                  <Brain className="h-6 w-6 text-violet-500" />
                </div>
                <h2 className="text-3xl font-bold">Thinking Modes</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Thinking Modes change how Arlo approaches your questions. Select a mode 
                before sending your message to get the type of response you need.
              </p>

              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <h3 className="font-semibold mb-1">🎯 Default</h3>
                  <p className="text-sm text-muted-foreground">
                    Balanced, helpful responses. Good for most questions and tasks.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h3 className="font-semibold mb-1">🧠 Deep Think</h3>
                  <p className="text-sm text-muted-foreground">
                    More thorough analysis with step-by-step reasoning. Best for complex 
                    problems, strategic decisions, or when you need detailed explanations.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h3 className="font-semibold mb-1">⚡ Quick</h3>
                  <p className="text-sm text-muted-foreground">
                    Fast, concise answers. Perfect for simple questions or when you're in a hurry.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h3 className="font-semibold mb-1">✍️ Creative</h3>
                  <p className="text-sm text-muted-foreground">
                    More imaginative and exploratory responses. Great for brainstorming, 
                    writing, or generating new ideas.
                  </p>
                </div>
              </div>
            </section>

            {/* Memories */}
            <section id="memories" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-pink-500/10 rounded-lg">
                  <Lightbulb className="h-6 w-6 text-pink-500" />
                </div>
                <h2 className="text-3xl font-bold">Memories</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Memories are facts and preferences that Arlo remembers about you across 
                all conversations. They help Arlo give more personalized and relevant responses.
              </p>

              <h3 className="font-semibold text-lg mb-3">How Memories Work</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Auto-Detection:</strong> Arlo suggests memories based on your conversations. You can accept or dismiss them.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Manual Entry:</strong> Add memories directly from the Memories panel in settings.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Categories:</strong> Memories are organized by type—preferences, facts, work context, etc.</span>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">Example Memories</h3>
              <div className="space-y-2 text-sm">
                <div className="p-3 bg-muted/50 rounded-lg">
                  "Prefers bullet points over long paragraphs"
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  "Works in fintech, focuses on B2B payments"
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  "Time zone: Pacific (PST/PDT)"
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  "Direct report: Sarah (Product Manager)"
                </div>
              </div>
            </section>

            {/* Knowledge Base */}
            <section id="knowledge-base" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
                <h2 className="text-3xl font-bold">Knowledge Base</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Upload documents to your Knowledge Base, and Arlo will reference them 
                when answering your questions. This is powerful for company-specific 
                information, product details, or reference materials.
              </p>

              <h3 className="font-semibold text-lg mb-3">Supported File Types</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1 bg-muted rounded-full text-sm">PDF</span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">Word (.docx)</span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">Text (.txt)</span>
                <span className="px-3 py-1 bg-muted rounded-full text-sm">Markdown (.md)</span>
              </div>

              <h3 className="font-semibold text-lg mb-3">How It Works</h3>
              <ol className="space-y-3 text-muted-foreground mb-6">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">1</span>
                  <span>Upload documents through Settings → Knowledge Base</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">2</span>
                  <span>Arlo processes and indexes the content using AI embeddings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">3</span>
                  <span>When you ask questions, Arlo searches for relevant sections</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">4</span>
                  <span>Relevant content is included in Arlo's context for accurate answers</span>
                </li>
              </ol>

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <h4 className="font-semibold text-blue-500 mb-2">Best Practices</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Keep documents focused on specific topics</li>
                  <li>• Use clear headings and structure in your documents</li>
                  <li>• Update documents when information changes</li>
                  <li>• Remove outdated documents to avoid confusion</li>
                </ul>
              </div>
            </section>

            {/* Email Integration */}
            <section id="emails" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Mail className="h-6 w-6 text-amber-500" />
                </div>
                <h2 className="text-3xl font-bold">Email Integration</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Bring your email conversations into Arlo for AI-assisted drafting, 
                summarization, and action item extraction. No complex setup required.
              </p>

              <h3 className="font-semibold text-lg mb-3">How It Works</h3>
              <ol className="space-y-3 text-muted-foreground mb-6">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">1</span>
                  <span>You get a unique Arlo email address (e.g., <code className="bg-muted px-1 rounded">inbox-abc123@m.heyarlo.ai</code>)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">2</span>
                  <span>CC this address on any email conversation you want Arlo to see</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">3</span>
                  <span>Emails appear in your Arlo Inbox, threaded automatically</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">4</span>
                  <span>Use AI actions to draft replies, summarize, or extract action items</span>
                </li>
              </ol>

              <h3 className="font-semibold text-lg mb-3">AI Actions</h3>
              <div className="space-y-3 mb-6">
                <div className="p-3 border border-border rounded-lg">
                  <h4 className="font-medium mb-1">📝 Draft Reply</h4>
                  <p className="text-sm text-muted-foreground">
                    Generate a professional reply based on the thread context and your communication style.
                  </p>
                </div>
                <div className="p-3 border border-border rounded-lg">
                  <h4 className="font-medium mb-1">📋 Summarize</h4>
                  <p className="text-sm text-muted-foreground">
                    Get a concise summary of the email thread with key points and current status.
                  </p>
                </div>
                <div className="p-3 border border-border rounded-lg">
                  <h4 className="font-medium mb-1">✅ Extract Actions</h4>
                  <p className="text-sm text-muted-foreground">
                    Pull out action items, deadlines, and responsibilities from the conversation.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <h4 className="font-semibold text-amber-500 mb-2">💡 Tips</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• CC Arlo early in a thread to capture the full context</li>
                  <li>• Reply-all keeps Arlo in the loop for ongoing conversations</li>
                  <li>• Drafts are for editing—Arlo never sends emails automatically</li>
                </ul>
              </div>
            </section>

            {/* Folders */}
            <section id="folders" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <FolderOpen className="h-6 w-6 text-green-500" />
                </div>
                <h2 className="text-3xl font-bold">Organization & Folders</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Keep your conversations organized with folders. Create folders for 
                different projects, clients, or topics.
              </p>

              <h3 className="font-semibold text-lg mb-3">Managing Folders</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Create:</strong> Click the folder icon in the sidebar to create a new folder.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Move Chats:</strong> Drag conversations into folders, or use the menu to move them.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Rename:</strong> Click on a folder name to edit it.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Delete:</strong> Remove folders (conversations inside are moved to the main list).</span>
                </li>
              </ul>
            </section>

            {/* Voice Input */}
            <section id="voice" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-red-500/10 rounded-lg">
                  <Mic className="h-6 w-6 text-red-500" />
                </div>
                <h2 className="text-3xl font-bold">Voice Input</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Speak your messages instead of typing. Click the microphone icon in the 
                chat input to start recording. Your speech is transcribed and sent to Arlo.
              </p>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2">How to Use</h4>
                <ol className="text-sm text-muted-foreground space-y-2">
                  <li>1. Click the microphone icon in the message input</li>
                  <li>2. Speak your message clearly</li>
                  <li>3. Click again to stop recording</li>
                  <li>4. Review the transcription and send</li>
                </ol>
              </div>
            </section>

            {/* Sharing */}
            <section id="sharing" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Share2 className="h-6 w-6 text-cyan-500" />
                </div>
                <h2 className="text-3xl font-bold">Sharing Conversations</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Share conversations with colleagues or collaborators via a secure link. 
                Great for sharing research, brainstorming sessions, or drafted content.
              </p>

              <h3 className="font-semibold text-lg mb-3">Sharing Options</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Generate Link:</strong> Create a shareable link from the conversation menu.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>View-Only:</strong> Shared links are read-only. Recipients can't edit or continue the conversation.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Expiration:</strong> Links can be set to expire after a certain time.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span><strong>Revoke:</strong> Disable a shared link at any time.</span>
                </li>
              </ul>
            </section>

            {/* Billing */}
            <section id="billing" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <CreditCard className="h-6 w-6 text-emerald-500" />
                </div>
                <h2 className="text-3xl font-bold">Billing & Subscriptions</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Arlo offers a free tier with usage limits, and paid plans for unlimited access.
              </p>

              <h3 className="font-semibold text-lg mb-3">Plans</h3>
              <div className="grid gap-4 md:grid-cols-2 mb-6">
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-semibold mb-2">Free</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• 20 messages per day</li>
                    <li>• Basic features</li>
                    <li>• 1 document upload</li>
                  </ul>
                </div>
                <div className="p-4 border border-primary/50 rounded-lg bg-primary/5">
                  <h4 className="font-semibold mb-2">Pro</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Unlimited messages</li>
                    <li>• All features</li>
                    <li>• Unlimited documents</li>
                    <li>• Priority support</li>
                  </ul>
                </div>
              </div>

              <h3 className="font-semibold text-lg mb-3">Managing Your Subscription</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span>View and manage your subscription in Settings → Billing</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span>Apply discount codes during checkout</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-primary mt-1" />
                  <span>Cancel anytime—your access continues until the end of the billing period</span>
                </li>
              </ul>
            </section>

            {/* Privacy & Security */}
            <section id="privacy" className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-slate-500/10 rounded-lg">
                  <Shield className="h-6 w-6 text-slate-500" />
                </div>
                <h2 className="text-3xl font-bold">Privacy & Security</h2>
              </div>

              <p className="text-muted-foreground mb-6">
                Your privacy is paramount. Here's how we protect your data.
              </p>

              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="h-5 w-5 text-green-500" />
                    <h3 className="font-semibold">Encryption</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    All data is encrypted in transit (TLS) and at rest (AES-256).
                  </p>
                </div>

                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-blue-500" />
                    <h3 className="font-semibold">No Training on Your Data</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Your conversations and documents are never used to train AI models.
                  </p>
                </div>

                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-violet-500" />
                    <h3 className="font-semibold">Access Control</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Only you can access your conversations, memories, and documents.
                  </p>
                </div>

                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="h-5 w-5 text-amber-500" />
                    <h3 className="font-semibold">Data Residency</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Data is stored in secure, SOC 2 compliant data centers.
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  For more details, see our{' '}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.
                </p>
              </div>
            </section>

            {/* Footer */}
            <div className="border-t border-border pt-8 mt-16">
              <p className="text-center text-muted-foreground">
                Need help? Contact us at{' '}
                <a href="mailto:hello@heyarlo.ai" className="text-primary hover:underline">
                  hello@heyarlo.ai
                </a>
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
